/**
 * TECNILAND - Authentication Service
 * Centraliza todas las llamadas al backend de autenticación
 * OPTIMIZED: console.error solo en desarrollo
 * PRODUCTION-READY: Usa variables de entorno
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://tecniland-backend.fly.dev';
const IS_PROD = process.env.NODE_ENV === 'production';

// Helper para logging seguro
const logError = (message, error) => {
  if (!IS_PROD) console.error(message, error);
};

/**
 * Servicio de autenticación para el backend de TECNILAND
 */
export const authService = {
  /**
   * Registra un nuevo usuario con access key obligatoria
   * @param {string} username - 3-16 chars, alfanumérico y guiones bajos
   * @param {string} email - formato email válido
   * @param {string} password - mínimo 8 caracteres
   * @param {string} accessKey - formato TECNI-XXXX-XXXX-XXXX-XXXX
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async register(username, email, password, accessKey) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, accessKey })
      });
      return response.json();
    } catch (error) {
      logError('Error en registro:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Inicia sesión con username y password
   * @param {string} username - nombre de usuario (case-insensitive)
   * @param {string} password - contraseña
   * @returns {Promise<Object>} Respuesta con token JWT si exitoso
   */
  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      return response.json();
    } catch (error) {
      logError('Error en login:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Valida un token JWT
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Datos del usuario si token válido
   */
  async validate(token) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.json();
    } catch (error) {
      logError('Error validando token:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Cierra la sesión actual
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Confirmación de logout
   */
  async logout(token) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.json();
    } catch (error) {
      logError('Error en logout:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Cierra todas las sesiones del usuario
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Número de sesiones cerradas
   */
  async logoutAll(token) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/sessions`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.json();
    } catch (error) {
      logError('Error cerrando sesiones:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Health check del servidor
   * @returns {Promise<Object>} Estado del servidor
   */
  async healthCheck() {
    try {
      const response = await fetch(`${API_BASE}/health`);
      return response.json();
    } catch (error) {
      logError('Error en health check:', error);
      return {
        success: false,
        error: 'Servidor no disponible',
        code: 'SERVER_OFFLINE'
      };
    }
  }
};

export default authService;
